import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '@modules/sessions/hooks/session';
import { useStageName } from '@modules/templates/hooks/stageName';

import { ToDestroy, ToShow } from '@modules/templates/sanitizers/stageName';

import { PER_PAGE, SEARCH_TYPES, SORT_TYPES } from '@shared/constants/shared';
import { BASE_URL } from '@modules/templates/constants/stageName';

import { TStageNameObject } from '@modules/templates/@types/TStageName';
import {
  TActionFunction,
  TColumnInfo,
  TFilterProps,
} from '@shared/components/TableList';
import { TFilterByOption } from '@shared/components/TableList/Header';

import { TLoggedBranch } from '@modules/sessions/@types/TSession';

import { TableList, Container, Page } from './styles';

const columnInfos: TColumnInfo[] = [
  { key: 'id', label: 'Código' },
  { key: 'name', label: 'Nome' },
  { key: 'type_view', label: 'Tipo' },
];

const filterByOptions: TFilterByOption[] = [
  { key: 'id', label: 'Código', type: 'normal', format: 'integer' },
  { key: 'name', label: 'Nome', type: 'normal', format: 'string' },
];

export function StageNameList(): JSX.Element {
  const [pageLoaded, setPageLoaded] = useState(false);

  const navigate = useNavigate();

  const { logged_branch } = useSession();
  const {
    list,
    show,
    destroy,
    validations,
    registerList,
    listTotal,
    listLoading,
  } = useStageName();

  function onSubmitSearch({
    filter_by,
    data,
    order,
    order_by,
    page,
    per_page,
  }: TFilterProps): void {
    const searchData = {
      order,
      order_by,
      page,
      per_page,
      for_selector: false,
      data:
        data && filter_by
          ? [
              {
                field: filter_by,
                type: SEARCH_TYPES.normal,
                values: data.split(';'),
              },
            ]
          : undefined,
    };

    list(searchData);
  }

  function addFunction() {
    navigate(`/${BASE_URL}/form`, { state: { creating: true } });
  }

  const actionFunctions = useMemo<TActionFunction<TStageNameObject>[]>(() => {
    return [
      {
        key: 'show',
        label: 'Visualizar',
        handle: (register: TStageNameObject) => {
          navigate(`/${BASE_URL}/form`, { state: { showing: true } });

          show({ register: ToShow({ register }) });
        },
      },
      {
        key: 'update',
        label: 'Atualizar',
        handle: (register: TStageNameObject) => {
          navigate(`/${BASE_URL}/form`, { state: { updating: true } });

          show({ register: ToShow({ register }) });
        },
      },
      {
        key: 'destroy',
        label: 'Remover',
        handle: (register: TStageNameObject) => {
          destroy({
            register: ToDestroy({
              register,
              logged_branch: logged_branch as TLoggedBranch,
            }),
          });
        },
        confirm_message: 'Deseja mesmo remover essa etapa/atividade?',
      },
    ];
  }, [navigate, show, destroy, logged_branch]);

  useEffect(() => {
    if (!registerList.length && !pageLoaded)
      list({
        order: SORT_TYPES.asc,
        order_by: 'id',
        page: 1,
        per_page: PER_PAGE,
        for_selector: false,
        data: [],
      });

    setPageLoaded(true);
  }, [list, registerList.length, pageLoaded]);

  return (
    <Page>
      <Container>
        <TableList
          title="Etapas e Atividades"
          registerList={registerList}
          listTotal={listTotal}
          addFunction={addFunction}
          columnInfos={columnInfos}
          onSubmitSearch={onSubmitSearch}
          filterByOptions={filterByOptions}
          actionFunctions={actionFunctions}
          validations={validations}
          loading={listLoading}
        />
      </Container>
    </Page>
  );
}
